import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { api, playerHeaders, useGameState, fmtClock, pointsIfCorrect, formatApiError } from "@/lib/api";
import { playDing, playBuzz, playFanfare, playAlert, playCheckIn, vibrate, unlockAudio } from "@/lib/sounds";
import Avatar from "@/components/Avatar";
import { toast } from "sonner";
import { Brain, Flame, Gift, Loader2, Radio, TrendingUp, Trophy, Users } from "lucide-react";
import { BadgeRow } from "@/components/Badges";

const LETTERS = ["1", "2", "3", "4"];

function WinOverlay({ me, taunt }) {
  const [spinning, setSpinning] = useState(false);
  const [prize, setPrize] = useState(null);

  useEffect(() => {
    playFanfare();
    vibrate([250, 120, 250, 120, 400]);
  }, []);

  async function spin() {
    setSpinning(true);
    try {
      const { data } = await api.post("/prizes/spin", {}, { headers: playerHeaders() });
      setPrize(data.prize);
      toast.success(`Prize won: ${data.prize.name}!`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSpinning(false);
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-navy-900/90 backdrop-blur-md"
      data-testid="play-win-overlay"
    >
      <motion.div
        initial={{ scale: 0.4, rotate: -10 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 14 }}
        className="flex max-w-sm flex-col items-center"
      >
        <Brain size={64} className="mb-4 text-amber" fill="#f5b335" />
        <Avatar icon={me.avatar_icon} color={me.avatar_color} config={me.avatar_config} prize={prize || (me.prizes || []).slice(-1)[0]} size={110} />
        <h2 className="mt-6 font-display text-4xl font-extrabold uppercase tracking-tight text-amber text-glow-amber">You win!</h2>
        <p className="mt-4 rounded-2xl bg-navy-800 px-6 py-3 text-center font-display text-xl font-bold text-cream">"{taunt || "Haha, I won!"}"</p>
        <div className="mt-4 w-full"><BadgeRow badges={me.badges} /></div>
        {me.guest ? (
          <p className="mt-5 max-w-xs text-center text-sm text-mut" data-testid="play-guest-prize-note">
            Create a full player profile next game to earn badges, prizes and spins.
          </p>
        ) : prize ? (
          <motion.p
            initial={{ scale: 0.6 }}
            animate={{ scale: 1 }}
            className="mt-5 rounded-2xl bg-amber/15 px-6 py-3 font-display text-lg font-extrabold uppercase text-amber-soft"
            data-testid="play-prize"
          >
            You won: {prize.name}
          </motion.p>
        ) : (
          <button
            data-testid="play-spin-button"
            onClick={spin}
            disabled={spinning}
            className="mt-5 flex items-center gap-2 rounded-full bg-amber px-8 py-3.5 font-display font-extrabold uppercase tracking-wide text-navy-900 transition-all hover:-translate-y-0.5 disabled:opacity-60"
          >
            {spinning ? <Loader2 size={16} className="animate-spin" /> : <Gift size={16} />}
            Spin the prize wheel
          </button>
        )}
      </motion.div>
    </motion.div>
  );
}

function RewardPopup({ reward, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/90 p-6 backdrop-blur-md"
      data-testid="play-reward-popup"
    >
      <motion.div
        initial={{ scale: 0.5, y: 40 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 200, damping: 15 }}
        className="w-full max-w-sm rounded-3xl border-2 border-amber/50 bg-navy-800 p-8 text-center shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
      >
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-amber/20">
          <Gift size={32} className="text-amber" />
        </div>
        <p className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-mint">Reward unlocked</p>
        <h2 className="mt-3 font-display text-3xl font-extrabold uppercase text-amber text-glow-amber" data-testid="play-reward-title">{reward.title}</h2>
        {reward.description && <p className="mt-2 text-cream">{reward.description}</p>}
        <p className="mt-4 text-sm text-mut">Earned at {reward.venue_name} — {reward.trigger === "wins" ? `${reward.games_required} first-place win${reward.games_required === 1 ? "" : "s"}` : `${reward.games_required} game${reward.games_required === 1 ? "" : "s"} played`}</p>
        <div className="mt-6 rounded-2xl border border-dashed border-amber/50 bg-amber/10 px-5 py-4 font-display font-extrabold uppercase tracking-wide text-amber-soft">
          Show this to the bartender
        </div>
        <button
          data-testid="play-reward-dismiss"
          onClick={onClose}
          className="mt-5 w-full rounded-full bg-amber py-3.5 font-display font-extrabold uppercase tracking-wide text-navy-900 transition-all hover:-translate-y-0.5"
        >
          Awesome — got it
        </button>
      </motion.div>
    </motion.div>
  );
}

function CheckInCard({ code, checkedIn }) {
  const [val, setVal] = useState("");
  const [status, setStatus] = useState("idle"); // idle | busy | ok | wrong

  async function submit(e) {
    e.preventDefault();
    if (status === "busy" || status === "ok") return;
    if (val.length !== 3) return;
    setStatus("busy");
    try {
      const { data } = await api.post(`/game/${code}/checkin`, { code: val }, { headers: playerHeaders() });
      if (data.ok) {
        setStatus("ok");
        playCheckIn();
        vibrate([200, 100, 200]);
      } else {
        setStatus("wrong");
        vibrate(300);
      }
    } catch (err) {
      setStatus("wrong");
    }
  }

  if (checkedIn || status === "ok") {
    return (
      <div className="w-full rounded-2xl border-2 border-mint/50 bg-mint/10 px-5 py-4 text-center" data-testid="play-checkin-success">
        <div className="font-display text-lg font-extrabold uppercase text-mint">You're checked in</div>
        <div className="mt-1 text-sm text-mut">See you when the next game starts — don't wander far.</div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="w-full rounded-2xl border-2 border-amber/50 bg-amber/10 px-5 py-4 text-center" data-testid="play-checkin-card">
      <div className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber">Still here?</div>
      <div className="mt-1 text-sm text-cream">Type the <span className="font-bold text-amber-soft">3-digit code</span> on the big screen to stay in the game</div>
      <input
        data-testid="play-checkin-input"
        aria-label="3-digit check-in code shown on the big screen"
        value={val}
        onChange={(e) => { setVal(e.target.value.replace(/\D/g, "").slice(0, 3)); if (status === "wrong") setStatus("idle"); }}
        placeholder="000"
        inputMode="numeric"
        autoComplete="one-time-code"
        autoFocus
        className={`font-mono-num mx-auto mt-3 block w-36 rounded-2xl border-2 bg-navy-900/80 px-4 py-3 text-center text-4xl font-bold tracking-[0.3em] text-cream outline-none transition-colors ${status === "wrong" ? "border-coral" : "border-ice/25 focus:border-amber"}`}
      />
      <button
        type="submit"
        data-testid="play-checkin-submit"
        disabled={status === "busy" || val.length !== 3}
        className="mt-3 w-full rounded-full bg-amber py-3 font-display font-extrabold uppercase tracking-wide text-navy-900 transition-all hover:-translate-y-0.5 disabled:opacity-50"
      >
        {status === "busy" ? <Loader2 size={16} className="mx-auto animate-spin" /> : "I'm here!"}
      </button>
      {status === "wrong" && (
        <p className="mt-2 text-sm font-bold text-coral" role="alert" data-testid="play-checkin-error">Wrong code — squint at the big screen and try again</p>
      )}
    </form>
  );
}

function BreakPhotos({ venue }) {
  const photos = (venue.specials || []).filter((s) => s.image_url).map((s) => ({ url: s.image_url, cap: s.title || "" }))
    .concat((venue.gallery || []).map((g) => ({ url: g.image_url || g, cap: g.caption || "" })));
  const [i, setI] = useState(0);

  useEffect(() => {
    if (photos.length < 2) return undefined;
    const iv = setInterval(() => setI((x) => x + 1), 4500);
    return () => clearInterval(iv);
  }, [photos.length]);

  if (!photos.length) return null;
  const p = photos[i % photos.length];
  return (
    <div className="relative h-44 w-full overflow-hidden rounded-2xl border border-ice/15 bg-navy-800" data-testid="play-break-photos">
      <AnimatePresence>
        <motion.img
          key={i}
          src={p.url}
          alt={p.cap || "Venue special"}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0 h-full w-full object-cover"
        />
      </AnimatePresence>
      {p.cap && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-navy-900/95 to-transparent px-4 pb-2.5 pt-8 text-left font-display text-sm font-extrabold uppercase text-cream" data-testid="play-break-photo-caption">
          {p.cap}
        </div>
      )}
    </div>
  );
}

export default function Play() {
  const { code } = useParams();
  const { state, secondsLeft } = useGameState(code, true);
  const [picked, setPicked] = useState(null);
  const [reward, setReward] = useState(null);
  const [cityRank, setCityRank] = useState(null);
  const [announce, setAnnounce] = useState("");
  const lastAlertRef = useRef(null);
  const [seenRewards, setSeenRewards] = useState(() => {
    try { return JSON.parse(localStorage.getItem("tk_seen_rewards") || "[]"); } catch (e) { return []; }
  });
  const qKey = state && !state.notFound ? `${state.game_number}:${state.q_index}` : null;

  // Fresh question: clear the pick AND the browser's focus ring so the last
  // answer's highlight never bleeds into the next question.
  useEffect(() => {
    setPicked(null);
    if (document.activeElement && document.activeElement.blur) document.activeElement.blur();
  }, [qKey]);

  useEffect(() => { unlockAudio(); }, []);

  const rewards = (state && state.me && state.me.rewards) || [];
  const unseenKey = rewards.map((r) => `${r.venue_code}:${r.earned_at}`).find((k) => !seenRewards.includes(k));

  useEffect(() => {
    if (unseenKey && !reward) {
      setReward(rewards.find((r) => `${r.venue_code}:${r.earned_at}` === unseenKey));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unseenKey]);

  useEffect(() => {
    if (!state || state.notFound || state.phase !== "break" || !code) return;
    let alive = true;
    api.get(`/game/${code}/boards`).then(({ data }) => {
      if (!alive) return;
      const rows = data.city || [];
      const idx = rows.findIndex((r) => r.venue_code === state.venue.code);
      if (idx >= 0) setCityRank({ rank: idx + 1, total: rows.length, region: data.metro || state.venue.region });
    }).catch(() => { /* board is optional on the phone */ });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state ? state.phase : null, code]);

  // Screen-reader narration — every phase change speaks the question & options.
  const livePhase = state && !state.notFound ? state.phase : null;
  const qIdx = state ? state.q_index : -1;
  const gameNum = state ? state.game_number : -1;
  useEffect(() => {
    if (!state || state.notFound) return;
    if (state.phase === "question" && state.question) {
      setAnnounce(`Question ${state.q_index + 1} of ${state.question_total}. ${state.question.text}. Options: ${state.question.options.map((o, i) => `${i + 1}. ${o}`).join(". ")}`);
    } else if (state.phase === "reveal" && state.question) {
      setAnnounce(`The correct answer is: ${state.question.options[state.question.correct]}. ${state.question.fact || ""}`);
    } else if (state.phase === "break") {
      setAnnounce(state.intermission_code ? "Intermission. Type the 3-digit check-in code shown on the big screen to stay in the game." : "Intermission. The next game starts soon.");
    } else if (state.phase === "leaderboard") {
      setAnnounce("Game over. Final standings are in.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePhase, qIdx, gameNum]);

  // Final 10 seconds of break: buzz + vibrate once a second so nobody misses kickoff.
  const secBucket = livePhase === "break" ? Math.ceil(secondsLeft) : -1;
  useEffect(() => {
    if (livePhase !== "break") { lastAlertRef.current = null; return; }
    if (secBucket <= 10 && secBucket > 0 && lastAlertRef.current !== secBucket) {
      lastAlertRef.current = secBucket;
      const urgent = secBucket <= 3;
      if (state.phone_sounds !== false) playAlert(urgent);
      vibrate(urgent ? [300, 120, 300] : 180);
      setAnnounce(`${secBucket} seconds until the next game starts`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePhase, secBucket]);

  function dismissReward() {
    if (reward) {
      const key = `${reward.venue_code}:${reward.earned_at}`;
      const next = [...seenRewards, key];
      setSeenRewards(next);
      try { localStorage.setItem("tk_seen_rewards", JSON.stringify(next)); } catch (e) { /* private mode */ }
    }
    setReward(null);
  }

  if (!state) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy-900">
        <div className="font-mono text-sm uppercase tracking-[0.3em] text-mut">Taking your seat…</div>
      </div>
    );
  }

  if (state.notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-navy-900 px-6 text-center" data-testid="play-not-found">
        <h1 className="font-display text-3xl font-extrabold uppercase text-cream">Room not found</h1>
        <p className="text-mut">Check the code on the big screen and try again.</p>
        <Link to="/join" className="rounded-full bg-amber px-8 py-3.5 font-display font-extrabold uppercase text-navy-900">Back to join</Link>
      </div>
    );
  }

  const me = state.me || { score: 0, rank: null, answered: false, avatar_icon: "Cat", avatar_color: "amber", name: "You" };
  const phase = state.phase;
  const frac = Math.max(0, Math.min(1, secondsLeft / (state.question_time || 10)));
  const isUrgent = secondsLeft <= 3;

  async function answer(choice) {
    if (picked !== null || me.answered) return;
    setPicked(choice);
    try {
      const { data } = await api.post(
        `/game/${code}/answer`,
        { q_index: state.q_index, choice },
        { headers: playerHeaders() }
      );
      toast[data.correct ? "success" : "error"](data.correct ? `Correct! +${data.points}` : "Wrong — 0 points");
      if (state.phone_sounds !== false) { if (data.correct) playDing(); else playBuzz(); }
      vibrate(data.correct ? 120 : 60);
    } catch (e) {
      toast.error(formatApiError(e));
      setPicked(null);
    }
  }

  return (
    <div className="relative flex min-h-screen flex-col bg-navy-900 text-cream"
      style={{ background: "radial-gradient(circle at 50% -10%, rgba(255,180,60,0.12), transparent 45%), radial-gradient(circle at 50% 110%, rgba(196,77,240,0.12), transparent 55%), #190a20" }}
      data-testid="play-page"
    >
      <header className="flex items-center justify-between border-b border-ice/10 px-4 py-3.5">
        <div>
          <div className="font-display text-sm font-extrabold uppercase tracking-wide">{state.venue.name}</div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-mut">
            Game <span className="text-amber">{String(state.game_number).padStart(4, "0")}</span> · Code {code}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {state.paused && (
            <span className="rounded-full bg-amber/20 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-amber-soft" data-testid="play-paused-chip">Paused</span>
          )}
          <div className="text-right">
            <div className="font-mono-num text-xl font-bold leading-none text-mint">{me.score}</div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-mut">pts · {me.rank ? `#${me.rank}` : "—"}</div>
          </div>
          <Avatar icon={me.avatar_icon} color={me.avatar_color} config={me.avatar_config} prize={(me.prizes || []).slice(-1)[0]} size={40} />
          <img src="/brand/braintap-logo-full.png" alt="braintap.bar" className="h-14 w-auto" data-testid="play-braintap-logo" />
        </div>
      </header>

      {state.venue.logo_url && (
        <div className="flex justify-center border-b border-ice/10 pb-2.5 pt-3" data-testid="play-venue-logo">
          <img src={state.venue.logo_url} alt={state.venue.name} className="h-14 max-w-[70%] object-contain" />
        </div>
      )}

      <main className="flex flex-1 flex-col px-4 py-5">
        {phase === "question" && state.question && (
          <div className="mx-auto flex w-full max-w-md flex-1 flex-col" data-testid="play-question">
            <div className="mb-4 flex items-center justify-between">
              <span className="rounded-full border border-amber/40 bg-amber/10 px-4 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber-soft">
                Question {state.q_index + 1} / {state.question_total}
              </span>
              <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-mut">
                <Radio size={12} className="text-coral" /> live
              </span>
            </div>
            <h1 className="mb-6 font-display text-2xl font-extrabold leading-snug" data-testid="play-question-text">{state.question.text}</h1>
            <div className="grid flex-1 grid-cols-1 gap-3">
              {state.question.options.map((opt, i) => {
                const answeredForMe = me.answered || picked !== null;
                return (
                  <button
                    key={i}
                    data-testid={`play-option-${i}`}
                    onClick={() => answer(i)}
                    disabled={answeredForMe}
                    className={`flex items-center gap-4 rounded-2xl border-2 p-4 text-left font-bold transition-all active:scale-[0.98] ${
                      answeredForMe
                        ? "border-ice/10 bg-navy-800/50 text-mut opacity-50"
                        : "border-ice/20 bg-navy-800/70 text-cream hover:border-amber hover:bg-amber/10"
                    }`}
                  >
                    <span className="font-mono-num flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#c44df0] to-[#8b2fd6] text-sm font-bold">{LETTERS[i]}</span>
                    {opt}
                  </button>
                );
              })}
            </div>

            {me.answered ? (
              <div className="mt-5 rounded-2xl border border-amber/40 bg-amber/10 px-5 py-4 text-center font-display font-extrabold uppercase tracking-wide text-amber-soft" data-testid="play-locked">
                Locked in · +{me.last_points} pts
                {me.streak >= 2 && (
                  <div className="mt-2 flex items-center justify-center gap-1.5 font-mono text-xs font-bold text-coral">
                    <Flame size={13} /> Streak ×{me.streak}
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-5">
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-navy-800">
                  <div className="h-full rounded-full" style={{ width: `${frac * 100}%`, background: "linear-gradient(90deg,#ff3d71,#ffb020,#ffd68a)", transition: "width 0.25s linear" }} />
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className={`font-mono-num text-2xl font-bold ${isUrgent ? "text-coral" : "text-amber-soft"}`} data-testid="play-timer">{Math.ceil(secondsLeft)}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-mut">
                    Win up to <span className="text-mint">{pointsIfCorrect(secondsLeft, state.question_time)}</span> pts
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {phase === "reveal" && state.question && (
          <div className="mx-auto flex w-full max-w-md flex-1 flex-col" data-testid="play-reveal">
            <span className="mb-4 w-fit rounded-full border border-mint/40 bg-mint/10 px-4 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-mint">Answer</span>
            <h1 className="mb-2 font-display text-2xl font-extrabold leading-snug">{state.question.text}</h1>
            <div className="mt-4 grid grid-cols-1 gap-3">
              {state.question.options.map((opt, i) => {
                const isCorrect = state.question.correct === i;
                return (
                  <div
                    key={i}
                    className={`flex items-center gap-4 rounded-2xl border-2 p-4 font-bold ${
                      isCorrect
                        ? "border-mint bg-mint/15 text-mint"
                        : "border-ice/10 bg-navy-800/50 text-mut opacity-50"
                    }`}
                    data-testid={`play-reveal-option-${i}`}
                  >
                    <span className="font-mono-num flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-navy-900 text-sm">{LETTERS[i]}</span>
                    {opt}
                    {isCorrect && <span className="ml-auto font-mono text-[10px] uppercase tracking-widest">correct</span>}
                  </div>
                );
              })}
            </div>
            {state.question.fact && (
              <div className="mt-6 rounded-2xl border-l-4 border-amber bg-navy-800/80 p-5" data-testid="play-fact">
                <div className="mb-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber">Did you know</div>
                <p className="text-sm leading-relaxed text-ice">{state.question.fact}</p>
              </div>
            )}
            <p className="mt-auto pt-6 text-center font-mono text-[11px] uppercase tracking-[0.3em] text-mut">Next question in {Math.ceil(secondsLeft)}s</p>
          </div>
        )}

        {phase === "leaderboard" && (
          <div className="mx-auto w-full max-w-md" data-testid="play-final">
            <h1 className="mb-6 text-center font-display text-2xl font-extrabold uppercase tracking-tight text-amber text-glow-amber">Final standings</h1>
            <div className="space-y-2.5">
              {state.standings.map((p, i) => (
                <div
                  key={p.player_id}
                  className={`flex items-center gap-3 rounded-2xl border p-3.5 ${p.player_id === me.player_id ? "border-amber bg-amber/10" : "border-ice/15 bg-navy-800/70"}`}
                  data-testid={`play-final-row-${i}`}
                >
                  <span className="font-mono-num w-7 text-center font-bold text-mut">{i + 1}</span>
                  <Avatar icon={p.avatar_icon} color={p.avatar_color} config={p.avatar_config} size={34} />
                  <span className="flex flex-1 items-center gap-2 truncate font-bold">
                    {p.name}{p.player_id === me.player_id ? " (you)" : ""}
                    {p.streak >= 2 && (
                      <span className="flex items-center gap-1 rounded-full bg-coral/15 px-2 py-0.5 font-mono text-[10px] font-bold text-coral">
                        <Flame size={11} />×{p.streak}
                      </span>
                    )}
                  </span>
                  <span className="font-mono-num font-bold text-mint">{p.score}</span>
                </div>
              ))}
            </div>
            <AnimatePresence>
              {me.rank === 1 && <WinOverlay me={me} taunt={state.taunt} />}
            </AnimatePresence>
          </div>
        )}

        {phase === "break" && (
          <div className="mx-auto flex w-full max-w-md flex-1 flex-col items-center justify-center text-center" data-testid="play-break">
            <img src="/brand/braintap-mascot.png" alt="BrainTap mascot" className="float-slow mb-4 w-24" data-testid="play-break-mascot" />
            <p className="font-mono text-[11px] font-bold uppercase tracking-[0.4em] text-mut">Intermission</p>
            <div className="font-mono-num my-6 text-7xl font-bold text-amber text-glow-amber" data-testid="play-break-clock">{fmtClock(secondsLeft)}</div>
            <p className="mb-6 max-w-xs text-mut">Grab a round. The next game starts automatically.</p>
            {me.absent && (
              <div className="mb-5 w-full rounded-2xl border-2 border-coral/50 bg-coral/10 px-5 py-4 text-center" data-testid="play-benched-card">
                <div className="font-display text-lg font-extrabold uppercase text-coral">You're benched this game</div>
                <div className="mt-1 text-sm text-mut">You missed the check-in — you're back in the line-up for the next game.</div>
              </div>
            )}
            {!me.absent && state.intermission_code && (
              <div className="mb-5 w-full"><CheckInCard code={code} checkedIn={me.checked_in} /></div>
            )}
            {state.intermission_code && state.checked_in_count > 0 && (
              <p className="mb-5 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-mut" data-testid="play-checkin-count">
                {state.checked_in_count} of {state.players_online} players checked in
              </p>
            )}
            <div className="mb-5 w-full"><BreakPhotos venue={state.venue} /></div>
            {cityRank && cityRank.total > 1 && cityRank.region && (
              <div className="mb-6 flex items-center gap-2 rounded-full border border-ice/20 bg-navy-800/70 px-5 py-2.5 text-center font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#c9a3ff]" data-testid="play-city-chip">
                <Trophy size={12} className="shrink-0" />
                {state.venue.name} is #{cityRank.rank} of {cityRank.total} bars in {cityRank.region} this week
              </div>
            )}
            {state.winner && (
              <div className="mb-8 flex items-center gap-4 rounded-2xl border border-amber/40 bg-amber/10 px-6 py-4">
                <Avatar icon={state.winner.avatar_icon} color={state.winner.avatar_color} size={44} />
                <div className="text-left">
                  <div className="font-display font-extrabold uppercase text-amber-soft">{state.winner.name} won last game</div>
                  <div className="text-sm text-mut">"{state.taunt}"</div>
                </div>
              </div>
            )}
            {state.loyalty && (
              <div className="mb-6 w-full" data-testid="play-loyalty-card">
                {rewards.some((r) => r.venue_code === code) ? (
                  <div className="rounded-2xl border-2 border-mint/50 bg-mint/10 px-5 py-4 text-center">
                    <div className="font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-mint">Your reward</div>
                    <div className="mt-1 font-display text-xl font-extrabold uppercase text-cream">{state.loyalty.title}</div>
                    <div className="mt-1 text-sm text-mint">Show this to the bartender</div>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-ice/15 bg-navy-800/70 px-5 py-4 text-left">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-cream">{state.loyalty.title}</span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-mut" data-testid="play-loyalty-progress">
                        {Math.min(state.loyalty.progress, state.loyalty.games_required)}/{state.loyalty.games_required} {state.loyalty.trigger === "wins" ? "wins" : "games"}
                      </span>
                    </div>
                    <div className="mt-2 h-2 overflow-hidden rounded-full bg-navy-900">
                      <div className="h-full rounded-full bg-mint transition-all" style={{ width: `${Math.min(1, state.loyalty.progress / state.loyalty.games_required) * 100}%` }} />
                    </div>
                    <div className="mt-2 text-xs text-mut">{state.loyalty.trigger === "wins" ? "Take first place here — the reward pops up on your phone" : "Keep playing here — the reward pops up on your phone once you hit the mark"}</div>
                  </div>
                )}
              </div>
            )}
            {state.venue.specials && state.venue.specials.length > 0 && (
              <div className="w-full text-left">
                <div className="mb-3 flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[0.3em] text-amber">
                  <TrendingUp size={12} /> Tonight's specials
                </div>
                <div className="space-y-2">
                  {state.venue.specials.slice(0, 3).map((s, i) => (
                    <div key={i} className="rounded-xl border border-ice/15 bg-navy-800/70 px-4 py-3">
                      <div className="text-sm font-bold text-cream">{s.title}</div>
                      <div className="text-xs text-mut">{s.description}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-8 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-mut">
              <Users size={12} /> {state.players_online} players in the room
            </div>
          </div>
        )}
      </main>

      <div className="sr-only" role="status" aria-live="assertive" aria-atomic="true" data-testid="play-announcer">{announce}</div>

      <footer className="border-t border-ice/10 px-4 py-3 text-center font-mono text-[10px] uppercase tracking-[0.25em] text-mut">
        braintap.bar · {state.venue.name}
      </footer>

      <AnimatePresence>
        {reward && <RewardPopup reward={reward} onClose={dismissReward} />}
      </AnimatePresence>
    </div>
  );
}
